import type { ThemeOptions } from '~/clc/features/academies/types';
import { Option } from '@workramp/ui';
import { useMemo } from 'react';
import { APP_CONSTANTS } from '~/common/explicit-exports';
import { useGetCustomPages } from '~/clc/features/academies/pages/api';
import { StyledSelect } from './DefaultLandingPageSelect.styles';

interface SelectDefaultPageProps {
  academyId: string;
  themeOptions: ThemeOptions;
  defaultLandingPage: string;
  onChange(value: string): void;
  certificationsEnabled: boolean;
  collectionsEnabled: boolean;
  eventsEnabled: boolean;
  pathsEnabled: boolean;
  pagesOrder: string[];
}

const LABEL = 'Default Landing Tab';

export const DefaultLandingPageSelect = ({
  academyId,
  themeOptions,
  defaultLandingPage,
  onChange,
  certificationsEnabled,
  collectionsEnabled,
  eventsEnabled,
  pathsEnabled,
  pagesOrder,
}: SelectDefaultPageProps) => {
  const { data: customPages } = useGetCustomPages(academyId);

  const options = useMemo(() => {
    const result = [];

    if (themeOptions.showMyTrainingsTab) {
      result.push({
        value: APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.MY_TRAININGS.KEY,
        label: themeOptions.pageTitles[APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.MY_TRAININGS.KEY] || 'My Trainings',
      });
    }

    if (collectionsEnabled) {
      result.push({
        value: APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.TRAININGS.KEY,
        label: themeOptions.pageTitles[APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.TRAININGS.KEY] || 'Collections',
      });
    }

    if (pathsEnabled) {
      result.push({
        value: APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.PATHS.KEY,
        label: themeOptions.pageTitles[APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.PATHS.KEY] || 'Paths',
      });
    }

    if (certificationsEnabled) {
      result.push({
        value: APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.CERTIFICATIONS.KEY,
        label: themeOptions.pageTitles[APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.CERTIFICATIONS.KEY] || 'Certifications',
      });
    }

    if (eventsEnabled) {
      result.push({
        value: APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.EVENTS.KEY,
        label: themeOptions.pageTitles[APP_CONSTANTS.ACADEMY_SUB_MENU_LINKS.EVENTS.KEY] || 'Events',
      });
    }

    result.push(
      ...(customPages ?? [])
        .filter(({ viewable, publishedAt }) => Boolean(viewable && publishedAt))
        .map(({ id, title }) => ({
          value: id,
          label: title,
        })),
    );

    result.sort((a, b) => pagesOrder.indexOf(a.value) - pagesOrder.indexOf(b.value));

    return result;
  }, [
    certificationsEnabled,
    collectionsEnabled,
    customPages,
    eventsEnabled,
    pagesOrder,
    pathsEnabled,
    themeOptions.pageTitles,
    themeOptions.showMyTrainingsTab,
  ]);

  return (
    <StyledSelect
      label={LABEL}
      title={LABEL}
      selectedKey={defaultLandingPage}
      onSelectionChange={onChange}
      tooltip="If the selected default landing tab is not visible, it will default to the first available tab."
    >
      {options.map(({ value, label }) => (
        <Option key={value}>{label}</Option>
      ))}
    </StyledSelect>
  );
};
